import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { Callout } from "@/components/ui/callout";
import { FormPageSkeleton } from "@/components/app/skeletons";
import { ArrivalScreen } from "@/features/arrival/components/arrival-screen";
import { formCodeOf, isFormSlug } from "@/features/arrival/forms";
import { formConfig } from "@nexseed/shared";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import {
  gatePassSearch,
  useGatePassPrefill,
} from "@/features/gate-pass-in/arrival-prefill";
import { GatePassMissing } from "@/features/gate-pass-in/components/gate-pass-missing";

/**
 * New Arrival is a screen, and it writes nothing.
 *
 * It used to be a redirect that POSTed a draft on mount, so every click — every misclick, every
 * back-button — left an empty arrival in the Pending tab that only a plant head could clear. The
 * document is now created by the first lot, header and all, in one request: until then the operator
 * is typing into a client-side shell and nothing exists server-side to abandon.
 *
 * The plant is taken from the reconciled list, never from the remembered id alone: posting a plant
 * that has since gone is what turns a stale browser into "facility not found".
 */
export const Route = createFileRoute("/_app/arrivals/$form/new")({
  params: {
    parse: ({ form }) => {
      if (!isFormSlug(form)) throw notFound();
      return { form };
    },
    stringify: (p) => p,
  },
  /**
   * `?dispatch=` is how the S-Capture Pending tab books a truck: the row knows which lorry was
   * tapped, and this screen would otherwise open blank and make the operator find its lots by hand.
   * `?gatePass=` is Gate › Inward Done starting an arrival against a Gate Pass In: the screen opens
   * with the pass's transport filled in, and the create links the two. Both optional, because most
   * forms reach New Arrival with nothing to say.
   */
  validateSearch: (
    search: Record<string, unknown>
  ): { dispatch?: string; gatePass?: number } => ({
    // The router JSON-parses search values, so a hand-typed `?dispatch=88` arrives as the number 88.
    // Dropping it opened a blank arrival with no word about the truck that was asked for.
    ...((typeof search.dispatch === "string" && search.dispatch) ||
    typeof search.dispatch === "number"
      ? { dispatch: String(search.dispatch) }
      : {}),
    ...gatePassSearch(search),
  }),
  beforeLoad: ({ params }) => {
    const auth = useAuth.getState();
    if (
      !auth.can("document.create") ||
      !auth.canViewForm(formCodeOf(params.form))
    )
      throw redirect({ to: "/" });
    // Set up on the handheld: the desk has no New arrival, and the API would refuse it anyway --
    // except PDN, whose desk starts a truck here from the supplier's dispatch sheet.
    const form = formConfig(formCodeOf(params.form));
    if (form.raisedOnHandheld && !form.declaredTypedAtSetup)
      throw redirect({ to: "/arrivals/$form", params: { form: params.form } });
  },
  component: Page,
});

function Page() {
  const { form } = Route.useParams();
  const { dispatch, gatePass } = Route.useSearch();
  const { facilities, facilityId, ready } = useFacilities();
  const prefill = useGatePassPrefill(gatePass);

  if (!ready || prefill.loading) return <FormPageSkeleton />;
  if (prefill.missing) return <GatePassMissing />;

  if (facilities.length === 0)
    return (
      <Callout tone="danger" title="No plants to receive into">
        This login can see no processing plants, so there is nowhere for an
        arrival to land. Add one under Masters → Processing Plant, or ask an
        administrator to give your account a plant.
      </Callout>
    );

  if (facilityId === null)
    return (
      <Callout tone="warning" title="Choose a plant first">
        An arrival is received at one plant. Pick one from the plant selector at
        the top of the screen, then raise the arrival again.
      </Callout>
    );

  return (
    <ArrivalScreen
      id={null}
      formCode={formCodeOf(form)}
      facilityId={facilityId}
      dispatch={dispatch}
      // A different pass is a different blank arrival, not an edit of the one on screen.
      key={prefill.passId ?? "blank"}
      initialHeader={prefill.initialHeader}
    />
  );
}
